import React, { useState } from "react";
import { Clock, RotateCcw, Check, X } from "lucide-react";
import { StaffViewer } from "@/components/StaffViewer";
import { SoundToggle } from "@/components/SoundToggle";
import { playResult, playFanfare } from "@/lib/feedbackSounds";
import { reportScore } from "@/lib/gameScores";

const DUR = { c: ["quarter"], m: ["half"], sb: ["whole"], dm: ["half", true], q: ["eighth"], dc: ["quarter", true] };
const PITCH = ["G4", "A4", "B4", "C5", "A4", "G4"];
const bar = (s) => s.split(" ").map((tok, k) => {
  const [kind, label = ""] = tok.split(":");
  const [duration, dot] = DUR[kind];
  return { pitch: PITCH[k % PITCH.length], duration, dot, label };
});
const Q = (ts, why, a, b, counts = false) => ({ ts, why, bars: [a, b], counts });

const BASIC = [
  Q("4/4", "Each bar adds up to 4 crotchet beats.", "c c c c", "m c c"), Q("4/4", "A semibreve is 4 beats, and 2 + 1 + 1 = 4.", "sb", "m c c"),
  Q("4/4", "2 + 2 = 4 and 1 + 1 + 2 = 4 crotchet beats.", "m m", "c c m"), Q("3/4", "Each bar adds up to 3 crotchet beats.", "c c c", "m c"),
  Q("3/4", "A dotted minim is 3 beats, and 1 + 2 = 3.", "c m", "dm"), Q("3/4", "2 + 1 = 3 and 1 + 1 + 1 = 3.", "m c", "c c c"),
  Q("2/4", "Each bar adds up to 2 crotchet beats.", "c c", "m"), Q("2/4", "2 crotchet beats in every bar.", "m", "c c"),
];
const ADVANCED = [
  Q("2/2", "Counted in minim beats: 1 2. That's 2 minim beats, so 2/2.", "m:1 m:2", "sb:1", true),
  Q("2/2", "The minims fall on 1 and 2, and the crotchets are on '1 &'. Minim beats mean 2/2.", "c:1 c:& m:2", "m:1 m:2", true),
  Q("6/8", "Six quavers counted 1 to 6, beamed in two groups of 3: that's 6/8.", "q:1 q:2 q:3 q:4 q:5 q:6", "dc:1 dc:4", true),
  Q("6/8", "Counted in quavers 1 to 6: crotchet (2) + quaver (1) twice = 6 quavers.", "c:1 q:3 c:4 q:6", "dm:1", true),
  Q("3/4", "Six quavers, but beamed in pairs and counted 1 & 2 & 3 &: 3 crotchet beats, so 3/4.", "q:1 q:& q:2 q:& q:3 q:&", "dm:1", true),
  Q("4/4", "Counted in crotchets 1 2 3 4: 4 crotchet beats, so 4/4.", "c:1 c:2 c:3 c:4", "m:1 m:3", true),
  Q("2/4", "Counted 1 2 in crotchets: 2 crotchet beats, so 2/4.", "c:1 c:2", "q:1 q:& c:2", true),
  Q("3/4", "Counted 1 2 3 in crotchets: 3 crotchet beats.", "c:1 m:2", "c:1 c:2 c:3", true),
  Q("2/2", "A semibreve is 2 minim beats, and two minims are counted 1 2: so 2/2.", "sb:1", "m:1 m:2", true),
  Q("6/8", "Quavers in groups of three, counted 1 2 3 4 5 6: so 6/8.", "dc:1 q:4 q:5 q:6", "q:1 q:2 q:3 dc:4", true),
  Q("4/4", "A dotted minim (3) + crotchet (1), counted 1 2 3 4 in crotchets: so 4/4.", "dm:1 c:4", "c:1 c:2 c:3 c:4", true),
  Q("3/4", "A dotted minim lasts 3 crotchet beats, counted 1 2 3: so 3/4.", "dm:1", "m:1 c:3", true),
];
const LEVEL = { basic: { bank: BASIC, options: ["2/4", "3/4", "4/4"] }, advanced: { bank: ADVANCED, options: ["2/4", "3/4", "4/4", "2/2", "6/8"] } };
const ROUND = 8;
const shuffle = (a) => [...a].sort(() => Math.random() - 0.5);

const notesOf = (q) => [...bar(q.bars[0]), { barline: true }, ...bar(q.bars[1]), { barline: "double" }];

// Match the time signature to two bars: count the beats (and, at the harder level, see how they're grouped).
export const TimeSigMatch = ({ level = "basic", testId = "time-sig-match" }) => {
  const cfg = LEVEL[level] || LEVEL.basic;
  const [qs, setQs] = useState(null);
  const [i, setI] = useState(0);
  const [score, setScore] = useState(0);
  const [pick, setPick] = useState(null);
  const q = qs?.[i];
  const start = () => { setQs(shuffle(cfg.bank).slice(0, ROUND)); setI(0); setScore(0); setPick(null); };
  const choose = (o) => {
    if (pick) return;
    setPick(o);
    const ok = o === q.ts;
    if (ok) setScore(score + 1);
    if (i === ROUND - 1) {
      const final = score + (ok ? 1 : 0);
      if (final === ROUND) { playResult(true); playFanfare(0.35, `Time signature match${level === "advanced" ? " (harder)" : ""}`); } else playResult(ok);
      reportScore(`time-sig-${level}`, `Time signature match${level === "advanced" ? " (harder)" : ""}`, "round", final);
    } else playResult(ok);
  };
  const done = qs && i === ROUND - 1 && pick;

  return (
    <div className="mt-4 rounded-2xl border border-border bg-card p-4 sm:p-5" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-primary"><Clock size={14} /> Time signature match</p>
        <span className="flex items-center gap-2">
          <SoundToggle testId={`${testId}-sound-toggle`} />
          {qs && <span className="whitespace-nowrap font-mono text-xs text-muted-foreground" data-testid={`${testId}-progress`}>{i + 1}/{ROUND} · Score {score}</span>}
        </span>
      </div>
      {!qs ? (
        <div className="mt-3">
          <p className="text-sm">{ROUND} questions. Look at the two bars{level === "advanced" ? " and how they're counted" : ""}, then pick the time signature that matches.</p>
          <button type="button" onClick={start} data-testid={`${testId}-start`} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">Start</button>
        </div>
      ) : (
        <>
          <div className="mt-3 overflow-x-auto rounded-xl border border-border bg-background/60 p-2">
            <div className="min-w-[420px]">
              <StaffViewer key={i} clef="treble" meter={q.ts} notes={notesOf(q)} width={560} showLabels={q.counts} playOnClick testId={`${testId}-staff`} />
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {cfg.options.map((o) => {
              const st = pick && (o === q.ts ? "right" : o === pick ? "wrong" : "");
              const [top, bottom] = o.split("/");
              return (
                <button key={o} type="button" onClick={() => choose(o)} data-testid={`${testId}-option-${o.replace("/", "-")}`} aria-label={`${top} ${bottom} time`}
                  className={`flex h-16 w-14 flex-col items-center justify-center rounded-xl border-2 font-serif text-xl font-bold leading-none transition-colors ${st === "right" ? "border-emerald-500 bg-emerald-500/15 text-emerald-500" : st === "wrong" ? "border-red-500 bg-red-500/15 text-red-500" : "border-border hover:border-primary"}`}>
                  <span>{top}</span><span>{bottom}</span>
                </button>
              );
            })}
          </div>
          {pick && (
            <div className={`mt-3 flex flex-wrap items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold ${pick === q.ts ? "bg-emerald-500/15 text-emerald-500" : "bg-red-500/15 text-red-500"}`} data-testid={`${testId}-feedback`}>
              {pick === q.ts ? <Check size={15} /> : <X size={15} />}
              <span>{pick === q.ts ? "Correct! " : `Not quite, it's ${q.ts}. `}{q.why}</span>
            </div>
          )}
          {pick && !done && (
            <button type="button" onClick={() => { setI(i + 1); setPick(null); }} data-testid={`${testId}-next`} className="mt-3 rounded-full bg-primary px-5 py-2 text-sm font-bold text-primary-foreground">Next</button>
          )}
          {done && (
            <div className="mt-3 flex flex-wrap items-center gap-3" data-testid={`${testId}-result`}>
              <p className="font-semibold">You got {score} out of {ROUND}{score === ROUND ? ". Perfect!" : "."}</p>
              <button type="button" onClick={start} data-testid={`${testId}-again`} className="inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-1.5 text-xs font-semibold hover:border-primary"><RotateCcw size={13} /> Play again</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
